import React, { useEffect, useState } from "react";
import { X, Smartphone, Share2 } from "lucide-react";

const DISMISS_KEY = "trueline_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS doesn't fire beforeinstallprompt — show manual instructions
    if (isIOS()) {
      const timer = setTimeout(() => setVisible(true), 4000); // wait 4s before showing
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        dismiss();
      }
      setDeferredPrompt(null);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 rounded-xl border border-blue-500/30 bg-[#0B1120]/95 backdrop-blur-md p-4 shadow-2xl shadow-blue-500/20"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-slate-500 hover:text-slate-300"
        data-testid="pwa-install-dismiss"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-4">
        <div className="rounded-lg bg-blue-500/15 p-2">
          <Smartphone className="w-5 h-5 text-blue-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-100">Instalar TrueLine no Início</div>
          {isIOS() ? (
            <div className="text-xs text-slate-400 mt-1 leading-relaxed">
              No Safari: toque em <Share2 className="w-3 h-3 inline-block -mt-0.5" /> <b>Compartilhar</b> → <b>Adicionar à Tela de Início</b>. O app abre em tela cheia, sem barras do Safari.
            </div>
          ) : (
            <div className="text-xs text-slate-400 mt-1 leading-relaxed">
              Adicione um atalho na tela inicial para abrir o TrueLine como um app nativo.
            </div>
          )}
          {deferredPrompt && (
            <button
              onClick={install}
              className="mt-3 text-xs uppercase tracking-wider font-semibold px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white"
              data-testid="pwa-install-confirm"
            >
              Instalar agora
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
